import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Reveal } from "./Reveal";
import { CATEGORIES } from "./categories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PostSomething = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialType = searchParams.get("type") === "offer" ? "offer" : "need";
  const [form, setForm] = useState({
    post_type: initialType,
    name: "",
    location: "",
    category: "skills-expertise",
    description: "",
    contact: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [posted, setPosted] = useState(false);
  const [matchCount, setMatchCount] = useState(0);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Enter your name or business name.";
    if (form.location.trim().length < 2) e.location = "Enter your town, city or region.";
    if (form.description.trim().length < 10) e.description = "Give a short description — at least 10 characters.";
    if (form.contact.trim().length < 3) e.contact = "Add an email, phone or social handle.";
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    const fieldErrors = validate();
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      toast.error("Please complete the highlighted fields.");
      const first = document.querySelector('[data-error="true"]');
      if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/posts`, form);
      setPosted(true);
      toast.success("Posted to the X.");
      axios
        .get(`${API}/matches`, { params: { post_id: res.data.id } })
        .then((r) => setMatchCount(r.data.length))
        .catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass = (key) => `field-input${errors[key] ? " !border-[#FFC700]/70" : ""}`;
  const FieldError = ({ field }) =>
    errors[field] ? (
      <p data-testid={`post-error-${field}`} className="mt-2 font-mono-accent text-[10px] tracking-[0.12em] uppercase text-[#FFC700]">
        {errors[field]}
      </p>
    ) : null;

  const isNeed = form.post_type === "need";

  return (
    <main data-testid="post-page" className="relative min-h-screen pt-32 pb-24 bg-grid overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: isNeed
            ? "radial-gradient(ellipse 50% 40% at 50% 0%, rgba(0,240,255,0.07), transparent 65%)"
            : "radial-gradient(ellipse 50% 40% at 50% 0%, rgba(138,43,226,0.1), transparent 65%)",
        }}
      />
      <div className="relative max-w-2xl mx-auto px-6">
        <Reveal>
          <span className="font-mono-accent text-xs tracking-[0.3em] text-[#00F0FF]" data-testid="post-eyebrow">
            /// POST TO THE X
          </span>
          <h1 className="mt-5 font-display font-extrabold uppercase text-3xl sm:text-4xl lg:text-5xl leading-[1.02] text-slate-100" data-testid="post-title">
            Put it out there. <span className="text-[#00F0FF]">Meet your match.</span>
          </h1>
          <p className="mt-5 text-slate-400 text-base sm:text-lg leading-relaxed">
            State what you need or what you can offer. Every post lands on the board where the
            other side is looking.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          {posted ? (
            <div className="card-frame mt-12 p-10 text-center" data-testid="post-success">
              <span className="mx-auto w-14 h-14 grid place-items-center border border-[#00F0FF]/60 text-[#00F0FF]">
                <Check size={26} />
              </span>
              <h2 className="mt-7 font-display font-extrabold uppercase text-2xl text-slate-100">
                You're on the board.
              </h2>
              <p className="mt-4 text-slate-400 text-sm sm:text-base leading-relaxed max-w-sm mx-auto">
                Your post is live on the Opportunities board. The other side of the X can now find you.
              </p>
              {matchCount > 0 && (
                <div
                  data-testid="post-match-banner"
                  className="mt-7 mx-auto max-w-sm border border-[#00F0FF]/40 bg-[#00F0FF]/5 px-5 py-4"
                >
                  <span
                    className="font-display font-extrabold uppercase text-sm text-transparent bg-clip-text"
                    style={{ backgroundImage: "linear-gradient(92deg, #00F0FF 10%, #8A2BE2 90%)" }}
                  >
                    ✕ Match Found
                  </span>
                  <p className="mt-2 font-mono-accent text-[11px] tracking-[0.12em] text-slate-400 uppercase">
                    {matchCount} {matchCount === 1 ? "post" : "posts"} on the board already {matchCount === 1 ? "matches" : "match"} yours
                  </p>
                </div>
              )}
              <div className="mt-9 flex flex-col sm:flex-row justify-center gap-4">
                <button data-testid="post-view-board-btn" onClick={() => navigate("/opportunities")} className="btn-primary">
                  View the Board
                </button>
                <button
                  data-testid="post-another-btn"
                  onClick={() => {
                    setPosted(false);
                    setForm({ ...form, description: "" });
                  }}
                  className="btn-ghost"
                >
                  Post Another
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} noValidate className="card-frame mt-12 p-7 sm:p-10 space-y-6" data-testid="post-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="post-type-group">
                <button
                  type="button"
                  data-testid="post-type-need"
                  onClick={() => setForm({ ...form, post_type: "need" })}
                  className={`px-4 py-4 font-mono-accent text-xs sm:text-sm uppercase tracking-[0.15em] border transition-all duration-300 ${
                    isNeed
                      ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/5 shadow-[0_0_24px_rgba(0,240,255,0.15)]"
                      : "border-white/10 text-slate-500 hover:border-white/30 hover:text-slate-300"
                  }`}
                >
                  I Need Something
                </button>
                <button
                  type="button"
                  data-testid="post-type-offer"
                  onClick={() => setForm({ ...form, post_type: "offer" })}
                  className={`px-4 py-4 font-mono-accent text-xs sm:text-sm uppercase tracking-[0.15em] border transition-all duration-300 ${
                    !isNeed
                      ? "border-[#8A2BE2] text-[#B57BEE] bg-[#8A2BE2]/10 shadow-[0_0_24px_rgba(138,43,226,0.25)]"
                      : "border-white/10 text-slate-500 hover:border-white/30 hover:text-slate-300"
                  }`}
                >
                  I Can Offer Something
                </button>
              </div>

              <div data-error={!!errors.name || undefined}>
                <label htmlFor="post-name" className="block font-mono-accent text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-2.5">
                  Name / Business Name
                </label>
                <input
                  id="post-name"
                  data-testid="post-name-input"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Carter Logistics Ltd"
                  className={fieldClass("name")}
                />
                <FieldError field="name" />
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div data-error={!!errors.location || undefined}>
                  <label htmlFor="post-location" className="block font-mono-accent text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-2.5">
                    Location
                  </label>
                  <input
                    id="post-location"
                    data-testid="post-location-input"
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="e.g. Surrey, UK"
                    className={fieldClass("location")}
                  />
                  <FieldError field="location" />
                </div>
                <div>
                  <label htmlFor="post-category" className="block font-mono-accent text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-2.5">
                    Category
                  </label>
                  <select
                    id="post-category"
                    data-testid="post-category-select"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="field-input appearance-none cursor-pointer"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value} className="bg-[#0A0D14]">
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div data-error={!!errors.description || undefined}>
                <label htmlFor="post-description" className="block font-mono-accent text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-2.5">
                  {isNeed ? "What do you need?" : "What can you offer?"}
                </label>
                <textarea
                  id="post-description"
                  data-testid="post-description-input"
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder={
                    isNeed
                      ? "e.g. Looking for a commercial yard within 10 miles of Guildford, 6–12 month term…"
                      : "e.g. 2,000 sq ft of secure warehouse space available immediately, racked and alarmed…"
                  }
                  className={`${fieldClass("description")} resize-none`}
                />
                <FieldError field="description" />
              </div>

              <div data-error={!!errors.contact || undefined}>
                <label htmlFor="post-contact" className="block font-mono-accent text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-2.5">
                  Contact Details
                </label>
                <input
                  id="post-contact"
                  data-testid="post-contact-input"
                  type="text"
                  value={form.contact}
                  onChange={(e) => setForm({ ...form, contact: e.target.value })}
                  placeholder="Email, phone or social handle"
                  className={fieldClass("contact")}
                />
                <FieldError field="contact" />
                <p className="mt-2 font-mono-accent text-[10px] tracking-[0.15em] text-slate-600">
                  NEVER SHOWN PUBLICLY — SHARED ONLY WHEN SOMEONE CONNECTS WITH YOUR POST
                </p>
              </div>

              <button
                type="submit"
                data-testid="post-submit-btn"
                disabled={submitting}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Transmitting…" : isNeed ? "Post My Need" : "Post My Offer"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </main>
  );
};
